import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import BackButton from '../BackButton';
import Button from '../Button';
import Icon from '../Icon';
import { formatClassName } from '../../Util';
import useContainerProps from '../../hooks/useContainerProps';
import { renderContainerChildren } from '../../util/RenderUtil';
import setTitle from '../../metatags/title';
import Dropdown from 'react-bootstrap/lib/Dropdown';
import MenuItem from 'react-bootstrap/lib/MenuItem';

/** renders a div that is styled to fill the header area */
const Header = props => {
  props = useContainerProps(props);
  const { back, button, className, data, icon, noBorder, noPadding, router, title, WrapperComponent } = props;

  function renderTitleBar() {
    if (!back && !icon && !title && !button) {
      return null;
    }

    if (WrapperComponent) {
      return (
        <WrapperComponent>
          <div className="header-titlebar" style={{ padding: 0 }}>
            {renderTitleBarInner()}
          </div>
        </WrapperComponent>
      );
    } else {
      return (
        <div className="header-titlebar">
          {renderTitleBarInner()}
        </div>
      );
    }
  }

  function renderTitleBarInner() {
    return (
      <>
        <div className="header-titlebar-left">
          {back && <BackButton action={back} router={router} data-back-arrow={true}/>}
          <h1 className="header-titlebar-title" {...getDataProps(data)}>
            {icon && (typeof icon === 'string') ? <Icon iconType={icon} additionalClasses="header-titlebar-icon" /> : icon}
            {title}
          </h1>
        </div>
        <div className="header-titlebar-right">
          {renderButtons()}
        </div>
      </>
    )
  }

  function renderButtons() {
    if (Array.isArray(button)) {
      return button.map(renderButton);
    } else if (button && button.action) {
      return renderButton(button);
    } else {
      return button;
    }
  }

  /* eslint-disable-next-line react/no-multi-comp */
  function renderButton(button, index) {
    const { icon, label, action } = button;
    if (!icon && !label) {
      return null;
    }

    const hasMultipleActions = Array.isArray(action) && action.length > 1;
    const handler = Array.isArray(action) && action.length === 1 ? action[0].action : action;

    return hasMultipleActions ? (
      <Dropdown data-type="dropdown">
        <Dropdown.Toggle className={`btn btn-${button.style}`} {...getDataProps(button.data)}>
          {label}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {button.action.map((item, idx) => (
            <MenuItem key={idx} onClick={item.action} {...getDataProps(item.data)}>
              {item.label}
            </MenuItem>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    ) : (
      <Button key={index} {...button} onClick={handler} />
    );
  }

  function getDataProps(data = {}) {
    const dataProps = {};
    if (data.action) {
      dataProps['data-action'] = data.action;
    }
    if (data.objectId) {
      dataProps['data-object-id'] = data.objectId;
    }
    if (data.objectType) {
      dataProps['data-object-type'] = data.objectType;
    }
    return dataProps;
  }

  useEffect(() => {
    if (title) {
      setTitle(title);
    }
  }, [title]);

  return (
    <div className={formatClassName(['header', className, { 'no-border': noBorder, 'no-padding': noPadding }])}>
      {renderTitleBar()}
      {renderContainerChildren(props, null, WrapperComponent)}
    </div>
  )
}

Header.propTypes = {
  back: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.func
  ]),
  button: PropTypes.oneOfType([
    PropTypes.shape(Button.propTypes),
    PropTypes.arrayOf(PropTypes.shape(Button.propTypes))
  ]),
  children: PropTypes.node,
  className: PropTypes.string,
  data: PropTypes.object,
  icon: PropTypes.string,
  noBorder: PropTypes.bool,
  noPadding: PropTypes.bool,
  router: PropTypes.shape({
    goBack: PropTypes.func
  }),
  title: PropTypes.string,
  WrapperComponent: PropTypes.elementType
};

export default Header;
